"use client";

import { c as _c } from "react/compiler-runtime";
import Image from "next/image";
import Link from "next/link";
import { User, Film, Clapperboard } from "lucide-react";
import { slugify } from "@/lib/slugify";
import type { SuggestionItem } from "@/types/search";
interface SearchSuggestionsProps {
  suggestions: SuggestionItem[];
  loading: boolean;
  activeIndex: number;
  onNavigate: () => void;
  isError?: boolean;
}

// null when there's nothing to link to yet — movie/series suggestions only
// carry `uuid` today, and the movie/series detail routes are keyed by
// unique_code, not uuid. Once the backend adds unique_code to suggestions,
// this starts returning real hrefs with no other change needed.
export function hrefFor(item: SuggestionItem): string | null {
  if (item.type === "person") return `/crew/${item.uuid}`;
  if (!item.unique_code) return null;
  const slug = slugify(item.title_en || item.title_fa);
  const suffix = slug ? `/${slug}` : "";
  return item.type === "series" ? `/s/${item.unique_code}${suffix}` : `/movie/${item.unique_code}${suffix}`;
}
const TYPE_ICONS: Record<SuggestionItem["type"], typeof User> = {
  person: User,
  series: Clapperboard,
  movie: Film
};
function SuggestionRow(t0) {
  const $ = _c(28);
  const {
    item,
    isActive,
    rounded,
    onNavigate
  } = t0;
  const Icon = TYPE_ICONS[item.type] ?? Film;
  let t1;
  if ($[0] !== item) {
    t1 = hrefFor(item);
    $[0] = item;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const href = t1;
  const t2 = `flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden bg-white/10 ${rounded === "full" ? "rounded-full" : "rounded-md"}`;
  let t3;
  if ($[2] !== Icon || $[3] !== item.poster_url || $[4] !== item.title_fa) {
    t3 = item.poster_url ? <Image src={item.poster_url} alt={item.title_fa || ""} width={32} height={32} className="h-full w-full object-cover" /> : <Icon className="h-4 w-4 text-gray-400" />;
    $[2] = Icon;
    $[3] = item.poster_url;
    $[4] = item.title_fa;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t2 || $[7] !== t3) {
    t4 = <span className={t2}>{t3}</span>;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const avatar = t4;
  const t5 = item.title_fa || item.title_en;
  let t6;
  if ($[9] !== t5) {
    t6 = <span className="w-full truncate text-sm font-medium text-white">{t5}</span>;
    $[9] = t5;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== item.subtitle) {
    t7 = item.subtitle && <span className="w-full truncate text-xs text-gray-500">{item.subtitle}</span>;
    $[11] = item.subtitle;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== t6 || $[14] !== t7) {
    t8 = <span className="flex max-w-44 min-w-0 flex-col items-start">{t6}{t7}</span>;
    $[13] = t6;
    $[14] = t7;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  const label = t8;
  const rowClass = `inline-flex max-w-full items-center gap-2 rounded-lg px-2.5 py-1.5 text-right transition-colors ${isActive ? "bg-white/10" : ""}`;
  if (!href) {
    const t9 = `${rowClass} cursor-default opacity-60`;
    let t10;
    if ($[16] !== avatar || $[17] !== isActive || $[18] !== label || $[19] !== t9) {
      t10 = <div className={t9} role="option" aria-disabled={true} aria-selected={isActive}>{avatar}{label}</div>;
      $[16] = avatar;
      $[17] = isActive;
      $[18] = label;
      $[19] = t9;
      $[20] = t10;
    } else {
      t10 = $[20];
    }
    return t10;
  }
  const t9 = `${rowClass} hover:bg-white/5`;
  let t10;
  if ($[21] !== avatar || $[22] !== href || $[23] !== isActive || $[24] !== label || $[25] !== onNavigate || $[26] !== t9) {
    t10 = <Link href={href} role="option" aria-selected={isActive} onClick={onNavigate} className={t9}>{avatar}{label}</Link>;
    $[21] = avatar;
    $[22] = href;
    $[23] = isActive;
    $[24] = label;
    $[25] = onNavigate;
    $[26] = t9;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  return t10;
}
export default function SearchSuggestions(t0) {
  const $ = _c(29);
  const {
    suggestions,
    loading,
    activeIndex,
    onNavigate,
    isError: t1
  } = t0;
  const isError = t1 === undefined ? false : t1;
  if (!loading && !isError && suggestions.length === 0) {
    return null;
  }
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  if ($[0] !== activeIndex || $[1] !== isError || $[2] !== loading || $[3] !== onNavigate || $[4] !== suggestions) {
    const persons = suggestions.filter(_temp);
    const content = suggestions.filter(_temp2);
    let t9;
    if ($[12] !== suggestions) {
      t9 = item => suggestions.indexOf(item);
      $[12] = suggestions;
      $[13] = t9;
    } else {
      t9 = $[13];
    }
    const indexOf = t9;
    t2 = "absolute inset-x-0 top-[calc(100%+0.5rem)] z-60 max-h-80 overflow-y-auto overflow-x-hidden rounded-xl border border-[#262f41] bg-[#0f1522] py-1.5 shadow-2xl shadow-black/40 animate-[searchSuggestFade_150ms_ease-out]";
    t3 = "listbox";
    if ($[14] !== loading || $[15] !== suggestions.length) {
      t4 = loading && suggestions.length === 0 && <div className="px-3 py-2.5 text-sm text-gray-400">در حال جستجو…</div>;
      $[14] = loading;
      $[15] = suggestions.length;
      $[16] = t4;
    } else {
      t4 = $[16];
    }
    if ($[17] !== isError || $[18] !== loading || $[19] !== suggestions.length) {
      t5 = !loading && isError && suggestions.length === 0 && <div className="px-3 py-2.5 text-sm text-red-300">خطا در دریافت پیشنهادها</div>;
      $[17] = isError;
      $[18] = loading;
      $[19] = suggestions.length;
      $[20] = t5;
    } else {
      t5 = $[20];
    }
    t6 = persons.length > 0 && <div className="py-1"><div className="px-3 pb-1 text-[11px] font-bold text-gray-500">بازیگران و عوامل</div><div className="flex flex-wrap gap-1 px-2">{persons.map(p => <SuggestionRow key={p.uuid} item={p} isActive={indexOf(p) === activeIndex} rounded="full" onNavigate={onNavigate} />)}</div></div>;
    t7 = persons.length > 0 && content.length > 0 && <div className="mx-3 my-1 border-t border-white/5" />;
    t8 = content.length > 0 && <div className="py-1"><div className="px-3 pb-1 text-[11px] font-bold text-gray-500">فیلم و سریال</div><div className="flex flex-wrap gap-1 px-2">{content.map(item_0 => <SuggestionRow key={item_0.uuid} item={item_0} isActive={indexOf(item_0) === activeIndex} rounded="md" onNavigate={onNavigate} />)}</div></div>;
    $[0] = activeIndex;
    $[1] = isError;
    $[2] = loading;
    $[3] = onNavigate;
    $[4] = suggestions;
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
    $[8] = t5;
    $[9] = t6;
    $[10] = t7;
    $[11] = t8;
  } else {
    t2 = $[5];
    t3 = $[6];
    t4 = $[7];
    t5 = $[8];
    t6 = $[9];
    t7 = $[10];
    t8 = $[11];
  }
  let t9;
  if ($[21] !== t2 || $[22] !== t3 || $[23] !== t4 || $[24] !== t5 || $[25] !== t6 || $[26] !== t7 || $[27] !== t8) {
    t9 = <div className={t2} role={t3}>{t4}{t5}{t6}{t7}{t8}</div>;
    $[21] = t2;
    $[22] = t3;
    $[23] = t4;
    $[24] = t5;
    $[25] = t6;
    $[26] = t7;
    $[27] = t8;
    $[28] = t9;
  } else {
    t9 = $[28];
  }
  return t9;
}
function _temp2(s_0) {
  return s_0.type !== "person";
}
function _temp(s) {
  return s.type === "person";
}